import { useCallback, useContext, useEffect, useState } from 'react';
import { ExamProps, StepProps } from '../../FormRisk.types';
import { examTypes, frequencyTypes } from './RecommendedExams.const';
import { JobProfileContext } from 'Views/JobProfile/JobProfile.context';
import Analytics from 'helpers/analytics';
import { JobProfileType } from 'Views/JobProfile/JobProfile.enums';

export const useRecommendedExams = ({
	recommended = [],
	steps,
}: {
	recommended: ExamProps[];
	steps: StepProps[];
}) => {
	const [currentPosition, setCurrentPosition] = useState<number>(0);
	const recommendedIds = recommended.map((item: ExamProps) => item.id);

	const {
		active,
		jobProfileType,
		isLoading,
		focusedId,
		jobRiskFactors,
		jobRiskFactorsById,
		setJobRiskFactors,
		jobRiskFactorsError,
		setJobRiskFactorsError,
	} = useContext(JobProfileContext);

	const { jobExamProfileRiskFactors } = jobRiskFactors?.[currentPosition]
		?.jobProfileRiskFactor ?? { jobExamProfileRiskFactors: undefined };

	const [frequencyTypesOptions, setFrequencyTypesOptions] = useState<
		{
			id: string;
			frequencyTypes: { id: string; value: string; disabled: boolean }[];
		}[]
	>();

	const isChecked = (id: string) => {
		return (
			jobExamProfileRiskFactors?.some((item) => item.exam.id === id) ?? false
		);
	};
	const handleAddExam = (exam: ExamProps) => {
		Analytics.track(
			'job-profile',
			`${
				jobProfileType === JobProfileType.RISK_PROFILE
					? '(Risk Profile)'
					: '(Exposition Area)'
			} (${steps[active].name} Risk) Recommended medical studies added`,
		);

		jobExamProfileRiskFactors.push({
			id: '',
			exam: exam,
			type: [],
			frequency: '',
		});
		setJobRiskFactors([...jobRiskFactors]);

		jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors.push({
			examId: exam.id,
			type: '',
			frequency: '',
		});
		setJobRiskFactorsError([...jobRiskFactorsError]);
	};

	const handleCheckRecommendedExam = (state: boolean, exam: ExamProps) => {
		if (state) {
			Analytics.track(
				'job-profile',
				`${
					jobProfileType === JobProfileType.RISK_PROFILE
						? '(Risk Profile)'
						: '(Exposition Area)'
				} (${steps[active].name} Risk) Recommended medical studies added`,
			);

			jobExamProfileRiskFactors.push({
				id: '',
				exam: exam,
				type: [],
				frequency: '',
			});
			setJobRiskFactors([...jobRiskFactors]);

			jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors.push({
				examId: exam.id,
				type: '',
				frequency: '',
			});
			setJobRiskFactorsError([...jobRiskFactorsError]);
		} else {
			Analytics.track(
				'job-profile',
				`${
					jobProfileType === JobProfileType.RISK_PROFILE
						? '(Risk Profile)'
						: '(Exposition Area)'
				} (${steps[active].name} Risk) Recommended medical studies deleted`,
			);

			const index = jobExamProfileRiskFactors.findIndex(
				(item) => item.exam.id === exam.id,
			);
			jobExamProfileRiskFactors.splice(index, 1);
			setJobRiskFactors([...jobRiskFactors]);

			const indexError = jobRiskFactorsError[
				currentPosition
			].jobExamProfileRiskFactors.findIndex((item) => item.examId === exam.id);
			jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors.splice(
				indexError,
				1,
			);
			setJobRiskFactorsError([...jobRiskFactorsError]);
		}
	};

	const handleDeleteExam = (id: string) => {
		Analytics.track(
			'job-profile',
			`${
				jobProfileType === JobProfileType.RISK_PROFILE
					? '(Risk Profile)'
					: '(Exposition Area)'
			} (${steps[active].name} Risk) Recommended medical studies deleted`,
		);

		const index = jobExamProfileRiskFactors.findIndex(
			(item) => item.exam.id === id,
		);
		jobExamProfileRiskFactors.splice(index, 1);
		setJobRiskFactors([...jobRiskFactors]);

		const indexError = jobRiskFactorsError[
			currentPosition
		].jobExamProfileRiskFactors.findIndex((item) => item.examId === id);
		jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors.splice(
			indexError,
			1,
		);
		setJobRiskFactorsError([...jobRiskFactorsError]);
	};

	const handleSelectExamType = (id: string, selected: { id: string }[]) => {
		const typesFormatted: string[] = selected.map((item) => item.id);
		const index = jobExamProfileRiskFactors.findIndex(
			(item) => item.exam.id === id,
		);

		const examByIndex = jobExamProfileRiskFactors?.[index];

		if (examByIndex) {
			let newFrequencyTypesOptions = [...(frequencyTypesOptions ?? [])];
			if (!typesFormatted.includes('periodic')) {
				newFrequencyTypesOptions = [
					{
						id: id,
						frequencyTypes: frequencyTypes.map((type) => {
							if (type.id === 'once') {
								return {
									id: type.id,
									value: type.value,
									disabled: false,
								};
							}
							return {
								id: type.id,
								value: type.value,
								disabled: true,
							};
						}),
					},
					...(newFrequencyTypesOptions ?? []),
				];
			} else {
				newFrequencyTypesOptions = [
					{
						id: id,
						frequencyTypes: frequencyTypes,
					},
				];
			}
			setFrequencyTypesOptions(
				[
					...new Set(newFrequencyTypesOptions.map((o) => JSON.stringify(o))),
				].map((s) => JSON.parse(s)),
			);

			jobExamProfileRiskFactors[index] = {
				...examByIndex,
				frequency:
					newFrequencyTypesOptions
						.find((exam) => exam.id === id)
						?.frequencyTypes.find(
							(frequency) =>
								frequency.id === examByIndex.frequency && !frequency.disabled,
						)?.id ?? '',
				type: [...typesFormatted],
			};
			setJobRiskFactors([...jobRiskFactors]);
		}

		const indexError = jobRiskFactorsError[
			currentPosition
		].jobExamProfileRiskFactors.findIndex((item) => item.examId === id);

		if (indexError !== -1) {
			jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors[
				indexError
			].type = '';
			setJobRiskFactorsError([...jobRiskFactorsError]);
		}
	};

	const handleSelectFrequency = (id: string, selected: { id: string }) => {
		const index = jobExamProfileRiskFactors.findIndex(
			(item) => item.exam.id === id,
		);

		const examByIndex = jobExamProfileRiskFactors?.[index];

		if (examByIndex) {
			jobExamProfileRiskFactors[index] = {
				...examByIndex,
				frequency: selected.id,
			};
			setJobRiskFactors([...jobRiskFactors]);
		}

		const indexError = jobRiskFactorsError[
			currentPosition
		].jobExamProfileRiskFactors.findIndex((item) => item.examId === id);

		if (indexError !== -1) {
			jobRiskFactorsError[currentPosition].jobExamProfileRiskFactors[
				indexError
			].frequency = '';
			setJobRiskFactorsError([...jobRiskFactorsError]);
		}
	};

	const handleChangeDays = (id: string, days: any) => {
		const index = jobExamProfileRiskFactors.findIndex(
			(item) => item.exam.id === id,
		);

		const examByIndex = jobExamProfileRiskFactors?.[index];

		if (examByIndex) {
			jobExamProfileRiskFactors[index] = {
				...examByIndex,
				days: days ?? 0,
			};

			setJobRiskFactors([...jobRiskFactors]);
		}
	};

	const getExamTypeValue = (id: string) => {
		const examObjectTypes =
			jobExamProfileRiskFactors.find((item) => item.exam.id === id)?.type ?? [];

		return examTypes.filter((type) => examObjectTypes.includes(type.id));
	};

	const getFrequencyValue = useCallback(
		(id: string) => {
			const frequencyObject = jobExamProfileRiskFactors.find(
				(item) => item.exam.id === id,
			);

			if (!frequencyObject?.frequency) return undefined;

			return frequencyTypes.find(
				(type) => type.id === frequencyObject?.frequency,
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[jobExamProfileRiskFactors, jobRiskFactors],
	);

	const getDaysInitialValue = (id: string) => {
		const frequencyObject = jobExamProfileRiskFactors.find(
			(item) => item.exam.id === id,
		);

		return frequencyObject?.frequency === 'other'
			? frequencyObject?.days
			: undefined;
	};

	useEffect(() => {
		const index = jobRiskFactorsById.jobRiskFactors.findIndex(
			(item) => item.id === focusedId,
		);

		setCurrentPosition(index);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusedId]);

	return {
		isLoading,
		recommendedIds,
		currentPosition,
		jobExamProfileRiskFactors,
		jobRiskFactorsError,
		frequencyTypesOptions,
		isChecked,
		handleAddExam,
		getExamTypeValue,
		handleChangeDays,
		handleDeleteExam,
		getFrequencyValue,
		getDaysInitialValue,
		handleSelectExamType,
		handleSelectFrequency,
		handleCheckRecommendedExam,
	};
};
